import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  Phone, PhoneOff, Play, Square, Mic, MicOff, Pause, PlayCircle,
  PhoneForwarded, Users, Disc, Save, ChevronRight, Zap,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { apiGet, apiPost } from '../lib/api.js';
import { fmtPhone } from '../lib/format.js';
import useMicrophoneAuth from '../hooks/useMicrophoneAuth.js';
import MicrophonePreflight from '../components/dialer/MicrophonePreflight.jsx';

const DISPOS = ['Hot', 'Warm', 'Cold', 'Sold', 'Not Interested', 'Bad Number', 'Do Not Call', 'Voicemail', 'Callback'];

function fmtDuration(s) {
  if (!s) return '00:00';
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
}

export default function Dialer() {
  const [params, setParams] = useSearchParams();
  const sessionParam = params.get('session');
  const providerParam = params.get('provider');

  const [mode, setMode] = useState('triple');
  const [session, setSession] = useState(null);
  const [activeCalls, setActiveCalls] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [hydrated, setHydrated] = useState(false);
  const [sessionPreview, setSessionPreview] = useState([]);

  // Hydrate any bulk-dial session passed via URL
  useEffect(() => {
    if (!sessionParam || hydrated) return;
    (async () => {
      try {
        const r = await apiGet(`/dialer/session/${sessionParam}`);
        setSession(r.session);
        setMode(r.session.mode || 'single');
        setSessionPreview(r.preview || []);
        toast.success(
          `${(providerParam || r.session.provider || '').toUpperCase()} session loaded — ${r.queueLength} leads queued`
        );
        setHydrated(true);
      } catch (e) {
        toast.error(e.response?.data?.error || 'Failed to load session');
        setHydrated(true);
        // Clear the bad param so reload doesn't loop
        const p = new URLSearchParams(params);
        p.delete('session');
        p.delete('provider');
        setParams(p, { replace: true });
      }
    })();
  }, [sessionParam, hydrated]); // eslint-disable-line

  // Softphone state for the focused call
  const [callSid, setCallSid] = useState(null);
  const [callStartedAt, setCallStartedAt] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [muted, setMuted] = useState(false);
  const [held, setHeld] = useState(false);
  const [recording, setRecording] = useState(true);
  const [transferTo, setTransferTo] = useState('');
  const [conferenceTo, setConferenceTo] = useState('');
  const [disposition, setDisposition] = useState('Hot');
  const [notes, setNotes] = useState('');
  const tickRef = useRef(null);

  const micAuth = useMicrophoneAuth();

  const { data, refetch } = useQuery({
    queryKey: ['dial-queue'],
    queryFn: () => apiGet('/dialer/queue'),
  });
  const queue = data?.queue || [];

  // Tick the call timer
  useEffect(() => {
    if (callStartedAt) {
      tickRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - callStartedAt) / 1000));
      }, 500);
      return () => clearInterval(tickRef.current);
    }
    setElapsed(0);
  }, [callStartedAt]);

  const focused = activeCalls[activeIdx] || null;

  async function startSession() {
    if (!micAuth.isReady) {
      const res = await micAuth.requestPermission();
      if (!res.ok) {
        toast.error('Microphone access is required to start dial session');
        return;
      }
    }
    const r = await apiPost('/dialer/start', { mode });
    setSession(r.session || { _id: 'demo-session', mode, callsAttempted: 0 });
    toast.success(`${mode}-line dialer started`);
  }

  async function nextDial() {
    if (!session) return;
    if (!micAuth.isReady) {
      const res = await micAuth.requestPermission();
      if (!res.ok) {
        toast.error('Microphone access is required to place next dial');
        return;
      }
    }
    try {
      const r = await apiPost(`/dialer/${session._id}/next`);
      if (r.done) { toast('Queue empty'); setSession(null); setActiveCalls([]); resetCall(); return; }
      setActiveCalls(r.calls || []);
      setActiveIdx(0);
      setSession(r.session || session);
      refetch();
      // Auto-arm the first call as the focused softphone leg
      if (r.calls && r.calls[0]) armCall(r.calls[0]);
    } catch (e) {
      toast.error('Next dial failed: ' + e.message);
    }
  }

  async function endSession() {
    if (!session) return;
    try {
      await apiPost(`/dialer/${session._id}/end`);
    } catch {}
    setSession(null);
    setActiveCalls([]);
    resetCall();
    toast('Session ended');
  }

  function resetCall() {
    setCallSid(null);
    setCallStartedAt(null);
    setMuted(false);
    setHeld(false);
    setRecording(true);
    setTransferTo('');
    setConferenceTo('');
    setNotes('');
    setDisposition('Hot');
  }

  async function armCall(call) {
    try {
      const r = await apiPost('/dialer/dial', {
        to: call.lead?.phone,
        from: '+14068604882',
        leadId: call.lead?._id,
      });
      setCallSid(r.callSid);
      setCallStartedAt(Date.now());
      setMuted(false);
      setHeld(false);
      setNotes('');
    } catch (e) {
      toast.error('Dial failed: ' + e.message);
    }
  }

  async function quickDial(lead) {
    if (!micAuth.isReady) {
      const res = await micAuth.requestPermission();
      if (!res.ok) {
        toast.error('Microphone access is required to place call');
        return;
      }
    }
    try {
      const r = await apiPost('/dialer/dial', { to: lead.phone, from: '+14068604882', leadId: lead._id });
      setActiveCalls([{ lead, call: { status: 'initiated', sid: r.callSid } }]);
      setActiveIdx(0);
      setCallSid(r.callSid);
      setCallStartedAt(Date.now());
      toast.success(`Calling ${lead.firstName} ${lead.lastName}`);
    } catch (e) {
      toast.error('Dial failed: ' + e.message);
    }
  }

  async function hangup() {
    if (!callSid) return;
    try {
      await apiPost('/dialer/hangup', { callSid });
      if (notes || disposition) {
        await apiPost('/dialer/dispose', { callSid, disposition, notes });
      }
      toast.success(`Call ended · disposed: ${disposition}`);
    } catch (e) {
      toast.error('Hangup failed: ' + e.message);
    }
    resetCall();
  }

  async function toggleMute() {
    if (!callSid) return;
    const next = !muted;
    try {
      await apiPost('/dialer/mute', { callSid, muted: next });
      setMuted(next);
    } catch (e) { toast.error(e.message); }
  }

  async function toggleHold() {
    if (!callSid) return;
    const next = !held;
    try {
      await apiPost('/dialer/hold', { callSid, held: next });
      setHeld(next);
    } catch (e) { toast.error(e.message); }
  }

  async function toggleRecord() {
    if (!callSid) return;
    const next = !recording;
    try {
      await apiPost('/dialer/record', { callSid, recording: next });
      setRecording(next);
      toast(next ? 'Recording on' : 'Recording paused');
    } catch (e) { toast.error(e.message); }
  }

  async function doTransfer() {
    if (!callSid || !transferTo) return;
    try {
      await apiPost('/dialer/transfer', { callSid, to: transferTo });
      toast.success(`Transferring to ${transferTo}`);
      setTransferTo('');
    } catch (e) { toast.error(e.message); }
  }

  async function doConference() {
    if (!callSid || !conferenceTo) return;
    try {
      await apiPost('/dialer/conference', { callSid, to: conferenceTo });
      toast.success(`Added ${conferenceTo} to conference`);
      setConferenceTo('');
    } catch (e) { toast.error(e.message); }
  }

  async function saveNotes() {
    if (!callSid) return;
    try {
      await apiPost('/dialer/dispose', { callSid, disposition, notes });
      toast.success('Notes saved');
    } catch (e) { toast.error(e.message); }
  }

  return (
    <div className="space-y-4 max-w-[1500px] mx-auto">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-civix-purpleSoft/80 uppercase tracking-[0.3em] text-xs">Power Dialer</div>
          <h1 className="font-display text-3xl font-bold mt-1">Softphone & AI Dialer Pool</h1>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {micAuth.isReady && (
            <MicrophonePreflight auth={micAuth} compact />
          )}
          <select disabled={!!session} value={mode} onChange={(e) => setMode(e.target.value)} className="input max-w-[160px]">
            <option value="single">Single line</option>
            <option value="double">Double line</option>
            <option value="triple">Triple line</option>
          </select>
          {!session ? (
            <button
              onClick={startSession}
              disabled={micAuth.permission === 'denied'}
              className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              title={micAuth.permission === 'denied' ? 'Unblock microphone in browser to dial' : 'Start Power Dialer session'}
            >
              <Play className="w-4 h-4"/>Start
            </button>
          ) : (
            <>
              <button
                onClick={nextDial}
                disabled={micAuth.permission === 'denied'}
                className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                title={micAuth.permission === 'denied' ? 'Unblock microphone in browser to dial' : 'Place next dial'}
              >
                <Phone className="w-4 h-4"/>Next Dial
              </button>
              <button onClick={endSession} className="btn-ghost flex items-center gap-2"><Square className="w-4 h-4"/>End</button>
            </>
          )}
        </div>
      </header>

      {/* Pre-Flight audio authorization banner when microphone is not yet granted or blocked */}
      {!micAuth.isReady && (
        <MicrophonePreflight auth={micAuth} />
      )}

      {/* Bulk-dial session banner */}
      {session && session.queue && session.queue.length > 0 && (
        <div className="panel p-3 border border-civix-purple/40 bg-linear-to-r from-civix-purple/10 to-civix-cyan/10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-linear-to-br from-civix-purple to-civix-cyan flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-civix-purpleSoft font-semibold">CIVIX bulk-dial session</div>
              <div className="text-sm text-white/90">
                {session.queue?.length || 0} leads queued · {mode}-line · {session.callsAttempted || 0} attempted
              </div>
            </div>
          </div>
          {sessionPreview.length > 0 && !callSid && (
            <div className="flex items-center gap-2 text-[11px] text-white/60">
              <span className="text-white/40">Up next:</span>
              <span className="text-white/90">{sessionPreview[0]?.firstName} {sessionPreview[0]?.lastName}</span>
              <span className="text-white/40 tabular-nums">{fmtPhone(sessionPreview[0]?.phone)}</span>
            </div>
          )}
        </div>
      )}

      {/* In-call softphone panel */}
      {callSid && focused && (
        <div className="panel p-6 border border-civix-cyan/30">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left: caller + timer */}
            <div className="space-y-3">
              <div className="text-xs uppercase tracking-[0.3em] text-civix-cyan flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-civix-cyan animate-pulse" />
                {held ? 'On hold' : 'Live call'}
              </div>
              <div className="font-display text-3xl font-bold">{focused.lead?.firstName} {focused.lead?.lastName}</div>
              <div className="text-white/60">{fmtPhone(focused.lead?.phone)}</div>
              <div className="text-xs text-white/40">{focused.lead?.state} · {focused.lead?.vertical || focused.lead?.productInterest || 'FEX'}</div>
              <div className="font-mono text-5xl gradient-text mt-4">{fmtDuration(elapsed)}</div>
              <div className="text-[10px] text-white/40 uppercase tracking-wider">SID {callSid.slice(0, 14)}…</div>
            </div>

            {/* Middle: call controls */}
            <div className="grid grid-cols-3 gap-3 self-center">
              <button onClick={toggleMute} className={`panel-light p-4 flex flex-col items-center gap-1 ${muted ? 'border border-amber-400/60 text-amber-300' : ''}`}>
                {muted ? <MicOff className="w-6 h-6"/> : <Mic className="w-6 h-6"/>}
                <span className="text-xs">{muted ? 'Unmute' : 'Mute'}</span>
              </button>
              <button onClick={toggleHold} className={`panel-light p-4 flex flex-col items-center gap-1 ${held ? 'border border-amber-400/60 text-amber-300' : ''}`}>
                {held ? <PlayCircle className="w-6 h-6"/> : <Pause className="w-6 h-6"/>}
                <span className="text-xs">{held ? 'Resume' : 'Hold'}</span>
              </button>
              <button onClick={toggleRecord} className={`panel-light p-4 flex flex-col items-center gap-1 ${recording ? 'text-red-400 border border-red-400/40' : ''}`}>
                <Disc className="w-6 h-6"/>
                <span className="text-xs">{recording ? 'Recording' : 'Rec off'}</span>
              </button>
              <div className="col-span-3 flex gap-2">
                <input value={transferTo} onChange={(e) => setTransferTo(e.target.value)} placeholder="+1 555 transfer" className="input flex-1" />
                <button onClick={doTransfer} className="btn-ghost flex items-center gap-1"><PhoneForwarded className="w-4 h-4"/>Xfer</button>
              </div>
              <div className="col-span-3 flex gap-2">
                <input value={conferenceTo} onChange={(e) => setConferenceTo(e.target.value)} placeholder="+1 555 conference" className="input flex-1" />
                <button onClick={doConference} className="btn-ghost flex items-center gap-1"><Users className="w-4 h-4"/>Conf</button>
              </div>
              <button onClick={hangup} className="col-span-3 bg-red-600 hover:bg-red-500 text-white py-3 rounded-lg flex items-center justify-center gap-2 font-semibold">
                <PhoneOff className="w-5 h-5"/> End Call
              </button>
            </div>

            {/* Right: dispose + notes */}
            <div className="space-y-3">
              <div>
                <div className="text-xs uppercase tracking-wider text-white/50 mb-1">Disposition</div>
                <select value={disposition} onChange={(e) => setDisposition(e.target.value)} className="input w-full">
                  {DISPOS.map((d) => <option key={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-white/50 mb-1">Call notes</div>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={6} className="input w-full" placeholder="Quick notes about the call…" />
              </div>
              <button onClick={saveNotes} className="btn-ghost w-full flex items-center justify-center gap-2"><Save className="w-4 h-4"/>Save Notes</button>
            </div>
          </div>
        </div>
      )}

      {/* Multi-line strip */}
      {session && activeCalls.length > 0 && (
        <div className="panel p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-sm text-white/50">Session #{String(session._id).slice(-6)} · {session.mode}-line</div>
              <div className="font-display text-xl font-semibold">{session.callsAttempted || activeCalls.length} dials made</div>
            </div>
            <div className="text-civix-cyan animate-pulse">● LIVE</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {activeCalls.map((c, i) => (
              <button
                key={i}
                onClick={() => { setActiveIdx(i); armCall(c); }}
                className={`panel-light p-3 text-left transition ${i === activeIdx ? 'border border-civix-cyan/60' : ''}`}
              >
                <div className="font-medium">{c.lead?.firstName} {c.lead?.lastName}</div>
                <div className="text-xs text-white/50">{fmtPhone(c.lead?.phone)} · {c.lead?.state}</div>
                <div className="text-xs text-civix-cyan mt-2 capitalize">{c.call?.status}{c.simulated ? ' (sim)' : ''}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* AI queue */}
      <div className="panel p-5">
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-display text-xl font-bold">AI-Curated Dial Queue</h2>
          <div className="text-xs text-white/40">click any lead to call directly</div>
        </div>
        <p className="text-sm text-white/50 mb-4">Score = recency × 0.3 + intent × 0.4 + value × 0.2 + priority × 0.1</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {queue.map(({ lead, score }) => (
            <button key={lead._id} onClick={() => quickDial(lead)} className="panel-light p-4 flex items-center justify-between hover:border-civix-cyan/40 border border-transparent transition text-left">
              <div>
                <div className="font-medium">{lead.firstName} {lead.lastName}</div>
                <div className="text-xs text-white/50">{fmtPhone(lead.phone)} · {lead.state} · {lead.productInterest || lead.vertical}</div>
              </div>
              <div className="text-right flex items-center gap-3">
                <div>
                  <div className="font-display text-lg gradient-text">{(score * 100).toFixed(0)}</div>
                  <div className="text-[10px] text-white/40 uppercase tracking-wider">score</div>
                </div>
                <ChevronRight className="w-4 h-4 text-white/40" />
              </div>
            </button>
          ))}
          {queue.length === 0 && <div className="text-white/40 text-sm">Queue is empty.</div>}
        </div>
      </div>
    </div>
  );
}
